import graphql__4750646a62ed78731b42 from "./__generated__/ImageBlockEditorUploadMutation.graphql.ts";import * as React from "react";
import {useState} from "react";
import Dropzone from "react-dropzone";
import {graphql, useMutation} from "react-relay";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowUpFromBracket} from "@fortawesome/pro-light-svg-icons";
import {useIntl} from "react-intl";
import {useErrorContext} from "../../contexts/ErrorContext";
import {EmailBlock} from "../../types/emailBlocks";

interface EmailTemplateImage {
    id: string;
    emailTemplateImageIdentifier: string;
    url: string;
    originalFilename: string;
    width: number;
    height: number;
    mediaType: string;
    fileSize: number;
}

interface ImageBlockEditorProps {
    block: EmailBlock;
    emailTemplateId: string;
    images: EmailTemplateImage[];
    onBlockChange: (updates: Partial<EmailBlock>) => void;
}

const ImagePlaceholder: React.FC<{className?: string}> = ({className}) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 640 640"
        className={className}
    >
        <path
            opacity=".4"
            fill="currentColor"
            d="M48 192L48 448C48 474.5 69.5 496 96 496L544 496C570.5 496 592 474.5 592 448L592 192C592 165.5 570.5 144 544 144L96 144C69.5 144 48 165.5 48 192zM184 240C184 262.1 166.1 280 144 280C121.9 280 104 262.1 104 240C104 217.9 121.9 200 144 200C166.1 200 184 217.9 184 240zM138.9 429.1C133.7 417.7 135.6 404.3 143.8 394.8L199.2 331C204.8 324.5 212.9 320.5 221.5 320C230.1 319.5 238.5 322.5 244.9 328.3L275.7 356.3L345.5 268.2C351.6 260.5 360.8 256.1 370.6 256.1C380.4 256.1 389.6 260.6 395.7 268.2L496.9 396C504.5 405.6 506 418.8 500.6 429.8C495.2 440.8 484.1 447.9 471.8 447.9L168 447.9C155.5 447.9 144.1 440.6 138.9 429.2z"
        />
        <path
            fill="currentColor"
            d="M544 144C570.5 144 592 165.5 592 192L592 448C592 474.5 570.5 496 544 496L96 496C69.5 496 48 474.5 48 448L48 192C48 165.5 69.5 144 96 144L544 144zM96 128C60.7 128 32 156.7 32 192L32 448C32 483.3 60.7 512 96 512L544 512C579.3 512 608 483.3 608 448L608 192C608 156.7 579.3 128 544 128L96 128zM144 264C130.7 264 120 253.3 120 240C120 226.7 130.7 216 144 216C157.3 216 168 226.7 168 240C168 253.3 157.3 264 144 264zM104 240C104 262.1 121.9 280 144 280C166.1 280 184 262.1 184 240C184 217.9 166.1 200 144 200C121.9 200 104 217.9 104 240zM358 278.1C361 274.3 365.7 272 370.5 272C375.3 272 380 274.2 383 278.1L484.4 405.9C488.2 410.7 488.9 417.3 486.3 422.8C483.7 428.3 478 431.8 471.9 431.8L168.1 431.8C161.8 431.8 156.1 428.1 153.5 422.4C150.9 416.7 151.9 410 156 405.3L211.4 341.4C214.2 338.1 218.2 336.2 222.5 335.9C226.8 335.6 231 337.1 234.2 340L271.3 373.7C272.9 375.2 275.1 375.9 277.3 375.8C279.5 375.7 281.5 374.5 282.9 372.8L358 278.1zM370.6 256C360.8 256 351.6 260.5 345.5 268.1L275.7 356.2L244.9 328.2C238.5 322.4 230.1 319.4 221.5 319.9C212.9 320.4 204.9 324.4 199.2 330.9L143.8 394.8C135.6 404.3 133.7 417.7 138.9 429.1C144.1 440.5 155.5 447.8 168 447.8L471.8 447.8C484.1 447.8 495.3 440.8 500.6 429.7C505.9 418.6 504.5 405.5 496.9 395.9L395.6 268.1C389.5 260.4 380.3 256 370.5 256z"
        />
    </svg>
);

const ImageBlockEditor: React.FC<ImageBlockEditorProps> = ({
    block,
    emailTemplateId,
    images,
    onBlockChange,
}) => {
    const intl = useIntl();
    const {addError} = useErrorContext();
    const [isUploading, setIsUploading] = useState(false);
    // Store recently uploaded image for immediate preview (before parent refreshes)
    const [recentlyUploadedImage, setRecentlyUploadedImage] = useState<EmailTemplateImage | null>(null);

    const isOverlayBlock = block.type === "imageFullWidthWithOverlay";
    // First check images from parent, then fall back to recently uploaded image
    const selectedImage = images.find(
        (img) => img.emailTemplateImageIdentifier === block.imageIdentifier
    ) || (recentlyUploadedImage?.emailTemplateImageIdentifier === block.imageIdentifier ? recentlyUploadedImage : null);

    const [commitUpload] = useMutation(graphql__4750646a62ed78731b42);

    const handleDrop = async (acceptedFiles: File[]) => {
        if (acceptedFiles.length === 0) return;

        setIsUploading(true);
        const file = acceptedFiles[0];

        try {
            const base64Data = await fileToBase64(file);

            commitUpload({
                variables: {
                    input: {
                        emailTemplateIdentifier: emailTemplateId,
                        data: base64Data,
                        filename: file.name,
                        mediaType: file.type,
                    },
                },
                onCompleted: (response: any) => {
                    if (response?.uploadEmailTemplateImage) {
                        const uploadedImage = response.uploadEmailTemplateImage;
                        // Store for immediate preview
                        setRecentlyUploadedImage(uploadedImage);
                        onBlockChange({
                            imageIdentifier: uploadedImage.emailTemplateImageIdentifier,
                        });
                    }
                    setIsUploading(false);
                },
                onError: (error) => {
                    console.error("Upload error:", error);
                    addError({
                        message: intl.formatMessage({id: "emailTemplate.images.uploadError"}),
                        type: "general",
                    });
                    setIsUploading(false);
                },
                updater: (store) => {
                    store.invalidateStore();
                },
            });
        } catch (error) {
            console.error("File reading error:", error);
            addError({
                message: intl.formatMessage({id: "emailTemplate.images.readError"}),
                type: "general",
            });
            setIsUploading(false);
        }
    };

    const fileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const result = reader.result as string;
                const base64 = result.split(",")[1];
                resolve(base64);
            };
            reader.onerror = (error) => reject(error);
        });
    };

    return (
        <div className="space-y-4">
            {/* Text Fields for Overlay Block */}
            {isOverlayBlock && (
                <div className="space-y-3">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {intl.formatMessage({id: "emailBlock.imageOverlay.headline"})}
                        </label>
                        <input
                            type="text"
                            value={block.headline || ""}
                            onChange={(e) => onBlockChange({headline: e.target.value})}
                            placeholder={intl.formatMessage({id: "emailBlock.imageOverlay.headlinePlaceholder"})}
                            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-xs focus:border-red-500 focus:ring-red-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {intl.formatMessage({id: "emailBlock.imageOverlay.subHeadline"})}
                        </label>
                        <input
                            type="text"
                            value={block.subHeadline || ""}
                            onChange={(e) => onBlockChange({subHeadline: e.target.value})}
                            placeholder={intl.formatMessage({id: "emailBlock.imageOverlay.subHeadlinePlaceholder"})}
                            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-xs focus:border-red-500 focus:ring-red-500"
                        />
                    </div>
                </div>
            )}

            {/* Alt Text */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {intl.formatMessage({id: "emailBlock.image.altText"})}
                </label>
                <input
                    type="text"
                    value={block.altText || ""}
                    onChange={(e) => onBlockChange({altText: e.target.value})}
                    placeholder={intl.formatMessage({id: "emailBlock.image.altTextPlaceholder"})}
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-xs focus:border-red-500 focus:ring-red-500"
                />
            </div>

            {/* Combined Image Upload Zone */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    {intl.formatMessage({id: "emailBlock.image.selectImage"})}
                </label>

                <Dropzone
                    accept={{
                        "image/jpeg": [".jpg", ".jpeg"],
                        "image/png": [".png"],
                        "image/gif": [".gif"],
                    }}
                    onDrop={handleDrop}
                    disabled={isUploading}
                    maxSize={10 * 1024 * 1024}
                    multiple={false}
                >
                    {({getRootProps, getInputProps, isDragActive}) => (
                        <div
                            className={`group relative w-full rounded-lg border-2 overflow-hidden transition-all cursor-pointer ${
                                isDragActive
                                    ? "border-red-400 bg-red-50"
                                    : "border-gray-200 hover:border-gray-300"
                            } ${isUploading ? "opacity-50 cursor-not-allowed" : ""} ${
                                !selectedImage ? "aspect-16/9" : ""
                            }`}
                            {...getRootProps()}
                        >
                            <input {...getInputProps()} />

                            {/* Image or Placeholder */}
                            {selectedImage ? (
                                <img
                                    src={selectedImage.url}
                                    alt={selectedImage.originalFilename}
                                    className="w-full max-h-64 object-contain"
                                />
                            ) : (
                                <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
                                    <ImagePlaceholder className="w-16 h-16 text-gray-300" />
                                </div>
                            )}

                            {/* Hover/Drag Overlay */}
                            <div
                                className={`absolute inset-0 flex flex-col items-center justify-center transition-all ${
                                    isDragActive
                                        ? "bg-red-500/20"
                                        : "bg-black/0 group-hover:bg-black/40"
                                }`}
                            >
                                <div
                                    className={`flex flex-col items-center transition-opacity ${
                                        isDragActive
                                            ? "opacity-100"
                                            : "opacity-0 group-hover:opacity-100"
                                    }`}
                                >
                                    <FontAwesomeIcon
                                        icon={faArrowUpFromBracket}
                                        className={`w-8 h-8 mb-2 ${
                                            isDragActive ? "text-red-600" : "text-white"
                                        }`}
                                    />
                                    <span
                                        className={`text-sm font-medium ${
                                            isDragActive ? "text-red-600" : "text-white"
                                        }`}
                                    >
                                        {isUploading
                                            ? intl.formatMessage({id: "emailTemplate.images.uploading"})
                                            : selectedImage
                                                ? intl.formatMessage({id: "emailBlock.image.replaceImage"})
                                                : intl.formatMessage({id: "emailBlock.image.uploadImage"})}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </Dropzone>

                <p className="mt-1.5 text-xs text-gray-500">
                    {intl.formatMessage({id: "emailTemplate.images.supportedFormats"})}
                </p>
            </div>
        </div>
    );
};

export default ImageBlockEditor;
